import axiosInstance from '../auth/axiosInstance';
import { assertApiSuccess, getApiErrorMessage, type ApiEnvelope } from '../types/api';
import type {
  AssessmentTemplateAssignment,
  AssessmentTemplateSummary,
  AssessmentTemplateVersionDetail,
  CloneAssessmentTemplateRequest,
  CreateAssessmentTemplateAssignmentRequest,
  RebuildTemplateResolutionsRequest,
  RebuildTemplateResolutionsResult,
  RestoreAssessmentTemplateVersionDefaultsRequest,
  SaveAssessmentTemplateVersionRequest,
  AssessmentTemplateVersionUsage,
} from '../types/dynamicAssessment';

export type {
  AssessmentLegacyFieldMap,
  AssessmentTemplateAssignment,
  AssessmentTemplateFieldDetail,
  AssessmentTemplateOptionDetail,
  AssessmentTemplateOptionSetDetail,
  AssessmentTemplateSectionDetail,
  AssessmentTemplateSummary,
  AssessmentTemplateVersionDetail,
  AssessmentTemplateVersionUsage,
  CloneAssessmentTemplateRequest,
  CreateAssessmentTemplateAssignmentRequest,
  RebuildTemplateResolutionsRequest,
  RebuildTemplateResolutionsResult,
  RestoreAssessmentTemplateVersionDefaultsRequest,
  SaveAssessmentTemplateVersionRequest,
} from '../types/dynamicAssessment';

/**
 * Lists active assessment templates.
 */
export const listAssessmentTemplates = async (
  assessmentTypeCode?: string
): Promise<AssessmentTemplateSummary[]> => {
  try {
    const { data } = await axiosInstance.get<ApiEnvelope<AssessmentTemplateSummary[]>>(
      '/AssessmentTemplate',
      {
        params: assessmentTypeCode ? { assessmentTypeCode } : undefined,
      }
    );

    return assertApiSuccess(data, { emptyOnNotFound: true }) ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Returns a template version including sections, fields, and option sets.
 */
export const getAssessmentTemplateVersion = async (
  versionId: number
): Promise<AssessmentTemplateVersionDetail> => {
  if (!versionId || versionId <= 0) {
    throw new Error('Please provide a valid version id.');
  }

  try {
    const { data } = await axiosInstance.get<ApiEnvelope<AssessmentTemplateVersionDetail>>(
      `/AssessmentTemplate/version/${versionId}`
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Clones a template and creates a new draft version from the latest source version.
 */
export const cloneAssessmentTemplate = async (
  request: CloneAssessmentTemplateRequest
): Promise<AssessmentTemplateVersionDetail> => {
  try {
    const { data } = await axiosInstance.post<ApiEnvelope<AssessmentTemplateVersionDetail>>(
      '/AssessmentTemplate/clone',
      request
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Creates or updates a draft template version graph.
 */
export const saveAssessmentTemplateVersion = async (
  request: SaveAssessmentTemplateVersionRequest
): Promise<AssessmentTemplateVersionDetail> => {
  try {
    const { data } = await axiosInstance.post<ApiEnvelope<AssessmentTemplateVersionDetail>>(
      '/AssessmentTemplate/version/save',
      request
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Publishes a draft template version.
 */
export const publishAssessmentTemplateVersion = async (
  versionId: number,
  publishedByUserId?: number
): Promise<AssessmentTemplateVersionDetail> => {
  if (!versionId || versionId <= 0) {
    throw new Error('Please provide a valid version id.');
  }

  try {
    const { data } = await axiosInstance.post<ApiEnvelope<AssessmentTemplateVersionDetail>>(
      `/AssessmentTemplate/version/${versionId}/publish`,
      null,
      {
        params: publishedByUserId ? { publishedByUserId } : undefined,
      }
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Creates a template assignment for a scope.
 */
export const createAssessmentTemplateAssignment = async (
  request: CreateAssessmentTemplateAssignmentRequest,
  createdByUserId?: number
): Promise<AssessmentTemplateAssignment> => {
  try {
    const { data } = await axiosInstance.post<ApiEnvelope<AssessmentTemplateAssignment>>(
      '/AssessmentTemplate/assign',
      request,
      {
        params: createdByUserId ? { createdByUserId } : undefined,
      }
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Lists active template assignments.
 */
export const listAssessmentTemplateAssignments = async (): Promise<
  AssessmentTemplateAssignment[]
> => {
  try {
    const { data } = await axiosInstance.get<ApiEnvelope<AssessmentTemplateAssignment[]>>(
      '/AssessmentTemplate/assignments'
    );

    return assertApiSuccess(data, { emptyOnNotFound: true }) ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Rebuilds the template resolution cache from active assignments.
 */
export const rebuildAssessmentTemplateResolutions = async (
  request: RebuildTemplateResolutionsRequest = {}
): Promise<RebuildTemplateResolutionsResult> => {
  try {
    const { data } = await axiosInstance.post<ApiEnvelope<RebuildTemplateResolutionsResult>>(
      '/AssessmentTemplate/rebuild-resolutions',
      request
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Returns response usage metadata for a template version.
 */
export const getAssessmentTemplateVersionUsage = async (
  versionId: number
): Promise<AssessmentTemplateVersionUsage> => {
  if (!versionId || versionId <= 0) {
    throw new Error('Please provide a valid version id.');
  }

  try {
    const { data } = await axiosInstance.get<ApiEnvelope<AssessmentTemplateVersionUsage>>(
      `/AssessmentTemplate/version/${versionId}/usage`
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Restores a draft template version to system defaults.
 */
export const restoreAssessmentTemplateVersionDefaults = async (
  versionId: number,
  request: RestoreAssessmentTemplateVersionDefaultsRequest = {}
): Promise<AssessmentTemplateVersionDetail> => {
  if (!versionId || versionId <= 0) {
    throw new Error('Please provide a valid version id.');
  }

  try {
    const { data } = await axiosInstance.post<ApiEnvelope<AssessmentTemplateVersionDetail>>(
      `/AssessmentTemplate/version/${versionId}/restore-defaults`,
      request
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};
